import { readStorage, removeStorage, writeStorage } from './safeStorage';
import { FLAG_ON, StorageKey } from './storageKeys';

/**
 * Local mode (glossary: 本机模式): the user chose on the welcome page to use the app in this browser only.
 * The choice is the device-local flag `wiztime.localOnly`; while it is set and sync is not configured the app
 * never contacts the server. The flag is kept in localStorage; this module adds change notification so the
 * welcome gate and the settings follow it in the same tab (other tabs are told by the `storage` event).
 */

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

export function isLocalMode(): boolean {
  return readStorage(StorageKey.LocalOnly) === FLAG_ON;
}

/**
 * Choose local mode. Returns false when the mode was not entered: the flag could not be stored (blocked site
 * data), or sync is configured on this device (local mode and sync exclude each other).
 */
export function enterLocalMode(): boolean {
  if (readStorage(StorageKey.Sync) !== null) return false;
  const stored = writeStorage(StorageKey.LocalOnly, FLAG_ON);
  notify();
  return stored;
}

/** Leave local mode: sync was activated, or the local data was cleared. */
export function leaveLocalMode(): void {
  removeStorage(StorageKey.LocalOnly);
  notify();
}

export function subscribeLocalMode(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent): void => {
    if (event.key === null || event.key === StorageKey.LocalOnly) listener();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}
